import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  PreviewLetterWrapper,
  PreviewLetterLogo,
  PreviewLetterContainer,
} from "../style";
import { UnderlyingContent } from "../../../style";
import { grantLetterFormatMap, getNameWithSalutation } from "../../../utils";
import previewLetter from "../../../../../../../assets/icons/preview-letter.svg";
import GrantPaymentLetter from "../../../../GrantAgreement/Components/GrantPaymentLetter/GrantPaymentLetter";
import Preloader from "../../../../../../../CommonComponents/Preloader/Preloader";
import { getPrimaryAccount } from "../../../../../../../utils/sessionUtil";
import { recordError } from "../../../../../../../utils/commonUtil";

export const conditionalDownloadRender = ({
  downloadLink,
  scheduleType,
  analyticsHandler,
  previewLetterHandler,
}) => {
  if (downloadLink) {
    return (
      <>
        <span>{"Grant payment letter generated. To download "}</span>
        <UnderlyingContent
          id="anrClickHereDwnld"
          href={downloadLink}
          target="_blank"
          onClick={() =>
            analyticsHandler &&
            analyticsHandler(
              "grantPaymentLetter2",
              "",
              `Grant Payment Letter Final - ${
                scheduleType === "recurring"
                  ? `Recurring Grant`
                  : `One Time Grant`
              }`
            )
          }
        >
          click here
        </UnderlyingContent>
        <span>.</span>
      </>
    );
  } else {
    return (
      <>
        <span>
          {
            "Your grant payment letter is been generated and will be updated soon. To generate a sample preview of the letter. "
          }
        </span>
        <UnderlyingContent
          id="anrClickHere"
          onClick={() => previewLetterHandler(analyticsHandler)}
        >
          click here
        </UnderlyingContent>
        <span>.</span>
      </>
    );
  }
};

export const PreviewLetter = ({
  charityName,
  charityAddress,
  startDate,
  grantAmount,
  purposeName,
  additionalComments,
  notifyFirstName,
  notifyLastName,
  inMemoryOfName,
  inHonorOfName,
  thirdParty,
  grantRecognition,
  advisors = [],
  publishName,
  includeAdvisorInfo,
  noBorderBottom,
  noPadding,
  noBorderTop,
  codeValueMappingData,
  sample = true,
  downloadLink,
  analyticsHandler,
  stepDesc,
  stepNum,
  status,
  scheduleType,
  missionaryName,
  missionaryLocation,
  projectName,
}) => {
  const [previewVisible, showPreview] = useState(false);
  const { accountName, userId: accountId } = getPrimaryAccount();
  const grantData = {
    granteeOrg: {
      name: charityName,
      ...charityAddress,
    },
    dateofPayment: startDate ? moment(startDate).valueOf() : undefined,
    grantAmount: grantAmount,
    grantFrom: getPrimaryAccount().accountName,
    primaryAdvisor: [],
    purpose: {
      purpose: purposeName,
      description: purposeName,
      additionalComments: additionalComments,
    },
    missionaryName: missionaryName,
    missionaryLocation: missionaryLocation,
    projectName: projectName,
    hideAdvisorInfo: !includeAdvisorInfo,
    thirdPartyDetails: thirdParty,
    grantRecognition: {
      grantRecognition: grantLetterFormatMap[grantRecognition], // Map from recognition id
      accountName,
      notifyFirstName,
      notifyLastName,
      inMemoryOfName,
      inHonorOfName,
      grantNotes: thirdParty,
    },
    publishPermission: publishName,
  };

  const previewLetterHandler = (analyticsHandler) => {
    try {
      showPreview(true);
      if (stepDesc) {
        analyticsHandler &&
          analyticsHandler(
            "grantPaymentLetter",
            `Step ${stepNum} ${stepDesc}`,
            `Grant Payment Letter Preview${
              scheduleType
                ? scheduleType === "recurring"
                  ? ` - Recurring Grant`
                  : ` - One Time Grant`
                : ""
            }`
          );
      } else {
        analyticsHandler &&
          analyticsHandler(
            "grantPaymentLetter2",
            "",
            `Grant Payment Letter Preview - ${
              scheduleType === "recurring"
                ? `Recurring Grant`
                : `One Time Grant`
            }`
          );
      }
    } catch (error) {
      recordError(error);
    }
  };

  advisors.map(
    ({ firstName, lastName, middleName, addresses = [], title, suffix }) => {
      const address = addresses.length ? addresses[0] : undefined;
      if (
        codeValueMappingData &&
        codeValueMappingData.title &&
        codeValueMappingData.title.length
      ) {
        grantData.primaryAdvisor.push({
          name: getNameWithSalutation(
            firstName,
            middleName,
            lastName,
            codeValueMappingData,
            title,
            suffix
          ),
          ...address,
        });
      }
    }
  );
  if (advisors && advisors.length) {
    return (
      <PreviewLetterWrapper
        noBorderTop={noBorderTop}
        noBorderBottom={noBorderBottom}
        noPadding={noPadding}
      >
        <PreviewLetterContainer flexCenter>
          <PreviewLetterLogo src={previewLetter} alt="preview-letter" />
          <PreviewLetterContainer contentStyle>
            {sample ? (
              <>
                <span>
                  {
                    "A letter will be generated and sent to the charity with each grant. To generate a sample preview of the letter "
                  }
                </span>
                <UnderlyingContent
                  id="anrClickHerePreviewLetter"
                  onClick={() => previewLetterHandler(analyticsHandler)}
                >
                  click here
                </UnderlyingContent>
                <span>.</span>
              </>
            ) : (
              conditionalDownloadRender({
                downloadLink,
                scheduleType,
                analyticsHandler,
                previewLetterHandler,
              })
            )}
          </PreviewLetterContainer>
        </PreviewLetterContainer>
        <GrantPaymentLetter
          visible={previewVisible}
          onCancel={() => showPreview(false)}
          grantData={grantData}
        ></GrantPaymentLetter>
      </PreviewLetterWrapper>
    );
  }
  return <Preloader />;
};

PreviewLetter.propTypes = {
  charityName: PropTypes.string,
  charityAddress: PropTypes.string,
  startDate: PropTypes.string,
  grantAmount: PropTypes.string,
  purposeName: PropTypes.string,
  additionalComments: PropTypes.string,
  notifyFirstName: PropTypes.string,
  notifyLastName: PropTypes.string,
  inMemoryOfName: PropTypes.string,
  inHonorOfName: PropTypes.string,
  thirdParty: PropTypes.string,
  grantRecognition: PropTypes.string,
  advisors: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      middleName: PropTypes.string,
      addresses: PropTypes.array,
      title: PropTypes.string,
      suffix: PropTypes.string,
    })
  ),
  publishName: PropTypes.bool,
  includeAdvisorInfo: PropTypes.bool,
  noBorderBottom: PropTypes.any,
  noPadding: PropTypes.bool,
  noBorderTop: PropTypes.bool,
  codeValueMappingData: PropTypes.object,
  sample: PropTypes.bool,
  downloadLink: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  analyticsHandler: PropTypes.func,
  stepDesc: PropTypes.any,
  stepNum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  status: PropTypes.any,
  scheduleType: PropTypes.string,
  missionaryName: PropTypes.string,
  missionaryLocation: PropTypes.string,
  projectName: PropTypes.string,
};

export default PreviewLetter;
